import { c as _c } from "react/compiler-runtime";
import { type MessageType, type CopilotMessageContentPart, type CopilotThinkingSignature } from '@precis-digital/kurama/MessageLayout';
import { type Theme } from '@precis-digital/kurama/theme';
import { useTheme } from '@precis-digital/kurama/utils';
import { CopilotUserMessageAttachments } from 'shared/copilot/components/CopilotUserMessageAttachments';
import { type Message as AgUiMessage, type ToolCall as AgUiToolCall } from '@ag-ui/client';
import { useCallback, useMemo, useRef, type ComponentProps, type ReactNode } from 'react';
import { AvatarWithLabel } from 'shared/components/AvatarWithLabel';
import { useAuth } from 'shared/context/AuthContext';
import { useCopilot } from 'shared/context/CopilotContext';
import { useCurrentClient } from 'shared/context/ClientContext';
import { useCopilotCsvFilenameIndex } from 'shared/copilot/api';
import { type JustSentImagePreview } from 'shared/copilot/hooks/useCopilotAgentConversation';
import { formatDateToRelativeFormat } from 'shared/dateFns';
import Markdown from 'shared/markdown';
import { CopilotSkillMessageContent } from 'shared/copilot/components/CopilotSkillMessageContent';
import { useQueryUserProfile } from 'src/profile/api';
import type * as clients from 'shared/api/clients';
import ToolCall from 'shared/copilot/components/ToolCall';
import { type CopilotChart } from 'shared/copilot/charts/dataChartTypes';
import { extractMarkdownTables } from 'shared/copilot/tables/parseMarkdownTable';
import type { CopilotPageElement } from '../pageContext/types';
import { useCopilotImagePreviewUrls } from 'shared/copilot/hooks/useCopilotImagePreviewUrls';
import { extractAttachedImagesFromUserContent, type AttachedMessageImage } from 'shared/copilot/utils/imageAttachment';
import { reduceAgUiMessagesToDrafts, type AgUiContentPartDraft, type ToolResultMessage } from './reduceAgUiMessagesToDrafts';
import { shouldResetArrivalTimesForChatIdChange, stampMessageArrivalTimes, freezeReasoningEndTimesIfSettled } from '../utils/stampMessageArrivalTimes';
type AssistantAgUiMessage = AgUiMessage & {
  role: 'assistant';
};

/** Optional fields some assistant payloads attach at runtime (not on core AgUiMessage types). */
type AssistantMessageWithGenerativeUi = AssistantAgUiMessage & {
  generativeUI?: () => ReactNode;
  generativeUIPosition?: 'before' | 'after';
};
type CompactMarkdownComponentType = (props: ComponentProps<typeof Markdown>) => ReactNode;
interface BuildMessagesFromAgUiArgs {
  messages: AgUiMessage[];
  currentUserName: string;
  currentUserDetails: clients.UserDetailsResp | undefined;
  isLoading: boolean;
  pageElements: CopilotPageElement[] | undefined;
  CompactMarkdownComponent: CompactMarkdownComponentType;
  theme: Theme;
  csvFilenameByGcsUri?: Readonly<Record<string, string>>;
  isCsvFilenameIndexLoading?: boolean;
  isCsvFilenameIndexError?: boolean;
  previewUrlByGcsUri?: Readonly<Record<string, string>>;
  filenameByGcsUri?: Readonly<Record<string, string>>;
  arePreviewUrlsLoading?: boolean;
  arePreviewUrlsError?: boolean;
  failedPreviewGcsUris?: readonly string[];
  refreshPreviewUrl?: (gcsUri: string) => Promise<string | null>;
  signPreviewUrlWithoutCaching?: (gcsUri: string) => Promise<{
    url: string;
    filename?: string;
  } | null>;
  justSentImagePreviewByGcsUri?: Readonly<Record<string, JustSentImagePreview>>;
  /**
   * Frozen end times for reasoning ids after a live run settles. Prevents
   * reasoning-only / stop from snapping thought duration to 0 on re-render.
   */
  reasoningEndedAtById?: ReadonlyMap<string, string>;
}

/**
 * Restricts the conversation-wide just-sent preview map to one message's own
 * images. `CopilotUserMessageAttachments` seeds its local state from this
 * once, on its own first render — passing the unfiltered map to every
 * message would seed each one with every *other* message's entries too,
 * which cost nothing while nothing ever revoked them, but became a hazard
 * once per-message cleanup was on the table (a message revoking a blob that
 * only looks unrelated to it, because it was never its own to begin with).
 */
const filterJustSentImagePreviewsForImages = (previewByGcsUri: Readonly<Record<string, JustSentImagePreview>>, images: AttachedMessageImage[]): Readonly<Record<string, JustSentImagePreview>> => {
  if (images.length === 0) return {};
  return Object.fromEntries(images.map((image): [string, JustSentImagePreview | undefined] => [image.gcsUri, previewByGcsUri[image.gcsUri]]).filter((entry): entry is [string, JustSentImagePreview] => entry[1] != null));
};
const getToolCallsById = (messages: AgUiMessage[]): Record<string, AgUiToolCall> => {
  return messages.reduce<Record<string, AgUiToolCall>>((acc, message) => {
    if (message.role !== 'assistant') return acc;
    message.toolCalls?.forEach(toolCall => {
      acc[toolCall.id] = toolCall;
    });
    return acc;
  }, {});
};
const getToolCallResultsById = (messages: AgUiMessage[]): Record<string, ToolResultMessage> => {
  return messages.filter((message): message is ToolResultMessage => message.role === 'tool').reduce<Record<string, ToolResultMessage>>((acc, message) => {
    acc[message.toolCallId] = message;
    return acc;
  }, {});
};
const getAssistantMessagesById = (messages: AgUiMessage[]): Record<string, AssistantMessageWithGenerativeUi> => {
  return messages.reduce<Record<string, AssistantMessageWithGenerativeUi>>((acc, message) => {
    if (message.role === 'assistant') {
      acc[message.id] = message as AssistantMessageWithGenerativeUi;
    }
    return acc;
  }, {});
};

/**
 * Renders assistant markdown and optional before/after generative UI for a message.
 * Omits empty content blocks while preserving generative UI ordering.
 */
const renderAssistantContent = (message: AssistantMessageWithGenerativeUi, CompactMarkdownComponent: CompactMarkdownComponentType, chartIdMap: Map<string, CopilotChart>): ReactNode => {
  const content = message.content == null ? '' : String(message.content);
  return <>
      {message.generativeUIPosition === 'before' && message.generativeUI?.()}
      {content.trim().length > 0 ? <CompactMarkdownComponent chartIdMap={chartIdMap}>{content}</CompactMarkdownComponent> : null}
      {message.generativeUIPosition === 'after' && message.generativeUI?.()}
    </>;
};
const renderThoughtSignatureContent = (content: ReactNode, CompactMarkdownComponent: CompactMarkdownComponentType): ReactNode => {
  if (typeof content !== 'string') return content;
  if (content.trim().length === 0) return content;
  return <CompactMarkdownComponent>{content}</CompactMarkdownComponent>;
};
const renderThinkingSignatures = (signatures: CopilotThinkingSignature[], CompactMarkdownComponent: CompactMarkdownComponentType): CopilotThinkingSignature[] => signatures.map(signature => signature.type === 'thought' ? {
  ...signature,
  content: renderThoughtSignatureContent(signature.content, CompactMarkdownComponent)
} : signature);
const renderContentPartDraft = (draft: AgUiContentPartDraft, toolCallsById: Record<string, AgUiToolCall>, toolCallResultsMap: Record<string, ToolResultMessage>, assistantsById: Record<string, AssistantMessageWithGenerativeUi>, CompactMarkdownComponent: CompactMarkdownComponentType): CopilotMessageContentPart | null => {
  if (draft.type === 'thinkingSignatures') {
    return {
      type: 'thinkingSignatures',
      id: draft.id,
      thinkingSignatures: renderThinkingSignatures(draft.thinkingSignatures, CompactMarkdownComponent),
      ...(draft.isThinkingInProgress === true ? {
        isThinkingInProgress: true
      } : {})
    };
  }
  if (draft.type === 'visibleTool') {
    const toolCall = toolCallsById[draft.toolCallId];
    const resultMessage = toolCallResultsMap[draft.toolCallId];
    if (toolCall == null || resultMessage == null) {
      return null;
    }
    return {
      type: 'content',
      id: draft.id,
      content: <ToolCall toolCall={toolCall} resultMessage={resultMessage} />
    };
  }
  const assistantMessage = assistantsById[draft.messageId];
  if (assistantMessage == null) {
    return null;
  }
  return {
    type: 'content',
    id: draft.id,
    content: renderAssistantContent(assistantMessage, CompactMarkdownComponent, new Map(draft.chartEntries))
  };
};

/**
 * Pure transform from AG-UI `Message[]` → Kurama `MessageType[]` ready for rendering.
 *
 * Reduces AG-UI events into serializable drafts (`reduceAgUiMessagesToDrafts`), then
 * renders drafts to Kurama messages (avatars, markdown, ToolCall cards).
 *
 * **Grouping rule:** Consecutive assistant/tool messages merge into one copilot bubble.
 * A user message or end of list flushes the current group. `message_end` tools flush
 * after thinking + text inside that bubble, and only once the agent is no longer
 * streaming (`isLoading` is false) so suggestion cards do not appear mid-prose.
 * Settled HITL results (`approved` / `rejected` / `failed`) stay visible during
 * follow-up runs so the applied card does not jump below new assistant text.
 */
export const buildMessagesFromAgUiMessages = ({
  messages,
  currentUserName,
  currentUserDetails,
  isLoading,
  pageElements,
  CompactMarkdownComponent,
  theme,
  csvFilenameByGcsUri = {},
  isCsvFilenameIndexLoading = false,
  isCsvFilenameIndexError = false,
  previewUrlByGcsUri = {},
  filenameByGcsUri = {},
  arePreviewUrlsLoading = false,
  arePreviewUrlsError = false,
  failedPreviewGcsUris = [],
  refreshPreviewUrl = async () => null,
  signPreviewUrlWithoutCaching = async () => null,
  justSentImagePreviewByGcsUri = {},
  reasoningEndedAtById
}: BuildMessagesFromAgUiArgs): MessageType[] => {
  const drafts = reduceAgUiMessagesToDrafts({
    messages,
    isLoading,
    pageElements,
    theme,
    csvFilenameByGcsUri,
    reasoningEndedAtById
  });
  const toolCallsById = getToolCallsById(messages);
  const toolCallResultsMap = getToolCallResultsById(messages);
  const assistantsById = getAssistantMessagesById(messages);
  return drafts.map((draft): MessageType => {
    if (draft.kind === 'user') {
      const userDate = new Date(draft.createdAtIso);
      return {
        id: draft.id,
        createdBy: {
          name: currentUserName,
          avatar: <AvatarWithLabel user={currentUserDetails} size="small" />
        },
        createdAt: userDate,
        createdAtString: formatDateToRelativeFormat(userDate),
        content: <>
            <CopilotSkillMessageContent content={draft.content} MarkdownComponent={CompactMarkdownComponent} />
            <CopilotUserMessageAttachments csvFilename={draft.attachedCsvFilename} csvGcsUri={draft.attachedCsvGcsUri} csvFilenameByGcsUri={csvFilenameByGcsUri} isCsvFilenameIndexLoading={isCsvFilenameIndexLoading} isCsvFilenameIndexError={isCsvFilenameIndexError} images={draft.attachedImages} previewUrlByGcsUri={previewUrlByGcsUri} filenameByGcsUri={filenameByGcsUri} arePreviewUrlsLoading={arePreviewUrlsLoading} arePreviewUrlsError={arePreviewUrlsError} failedPreviewGcsUris={failedPreviewGcsUris} refreshPreviewUrl={refreshPreviewUrl} signPreviewUrlWithoutCaching={signPreviewUrlWithoutCaching} justSentImagePreviewByGcsUri={filterJustSentImagePreviewsForImages(justSentImagePreviewByGcsUri, draft.attachedImages)} />
          </>,
        isCurrentUser: true,
        messageType: 'user'
      };
    }
    const contentParts = draft.contentParts.map(part => renderContentPartDraft(part, toolCallsById, toolCallResultsMap, assistantsById, CompactMarkdownComponent)).filter((part): part is CopilotMessageContentPart => part != null);
    const copilotDate = new Date(draft.createdAtIso);
    return {
      id: draft.id,
      createdAt: copilotDate,
      createdAtString: formatDateToRelativeFormat(copilotDate),
      contentParts,
      isCurrentUser: false,
      messageType: 'copilot',
      ...(draft.isThinkingInProgress === true ? {
        isThinkingInProgress: true
      } : {})
    };
  });
};

/**
 * Hook that returns a stable callback for converting AG-UI messages to Kurama
 * `MessageType[]`. Binds the current user's identity and loading state so
 * `buildMessagesFromAgUiMessages` doesn't need them passed at every call site.
 */
export const useCreateMessagesFromAgUiMessages = () => {
  const $ = _c(22);
  const theme = useTheme();
  const {
    currentUser
  } = useAuth();
  const {
    currentClient
  } = useCurrentClient();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      throwOnError: false
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    data: currentUserDetails
  } = useQueryUserProfile(currentUser?.id, t0);
  const {
    filenameByGcsUri: csvFilenameByGcsUri,
    isLoading: isCsvFilenameIndexLoading,
    isError: isCsvFilenameIndexError
  } = useCopilotCsvFilenameIndex(currentClient.id, currentUser?.id);
  const {
    isLoading,
    currentPageContext,
    messages: conversationMessages,
    justSentImagePreviewByGcsUri,
    currentChatId
  } = useCopilot();
  const pageElements = currentPageContext?.elements;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Map();
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const arrivalTimesByIdRef = useRef(t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = new Map();
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const reasoningEndedAtByIdRef = useRef(t2);
  const wasLoadingRef = useRef(false);
  const arrivalTimesChatIdRef = useRef(currentChatId);
  let t3;
  if ($[3] !== conversationMessages) {
    t3 = conversationMessages.flatMap(_temp2);
    $[3] = conversationMessages;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const attachedImageUris = t3;
  const {
    previewUrlByGcsUri,
    filenameByGcsUri,
    isLoading: arePreviewUrlsLoading,
    isError: arePreviewUrlsError,
    failedGcsUris: failedPreviewGcsUris,
    refreshPreviewUrl,
    signWithoutCaching: signPreviewUrlWithoutCaching
  } = useCopilotImagePreviewUrls(currentClient.id, attachedImageUris);
  const Component = _temp4;
  const CompactMarkdownComponent = Component;
  let t4;
  if ($[5] !== arePreviewUrlsError || $[6] !== arePreviewUrlsLoading || $[7] !== csvFilenameByGcsUri || $[8] !== currentChatId || $[9] !== currentUserDetails || $[10] !== failedPreviewGcsUris || $[11] !== filenameByGcsUri || $[12] !== isCsvFilenameIndexError || $[13] !== isCsvFilenameIndexLoading || $[14] !== isLoading || $[15] !== justSentImagePreviewByGcsUri || $[16] !== pageElements || $[17] !== previewUrlByGcsUri || $[18] !== refreshPreviewUrl || $[19] !== signPreviewUrlWithoutCaching || $[20] !== theme) {
    t4 = messages => {
      if (shouldResetArrivalTimesForChatIdChange(arrivalTimesChatIdRef.current, currentChatId)) {
        arrivalTimesByIdRef.current = new Map();
        reasoningEndedAtByIdRef.current = new Map();
      }
      if (arrivalTimesChatIdRef.current !== currentChatId) {
        arrivalTimesChatIdRef.current = currentChatId;
      }
      const stampedMessages = stampMessageArrivalTimes(messages, arrivalTimesByIdRef.current);
      freezeReasoningEndTimesIfSettled(stampedMessages, reasoningEndedAtByIdRef.current, {
        wasLoading: wasLoadingRef.current,
        isLoading
      });
      wasLoadingRef.current = isLoading;
      return buildMessagesFromAgUiMessages({
        messages: stampedMessages,
        currentUserName: currentUserDetails?.name ?? "",
        currentUserDetails,
        isLoading,
        pageElements,
        CompactMarkdownComponent,
        theme,
        csvFilenameByGcsUri,
        isCsvFilenameIndexLoading,
        isCsvFilenameIndexError,
        previewUrlByGcsUri,
        filenameByGcsUri,
        arePreviewUrlsLoading,
        arePreviewUrlsError,
        failedPreviewGcsUris,
        refreshPreviewUrl,
        signPreviewUrlWithoutCaching,
        justSentImagePreviewByGcsUri,
        reasoningEndedAtById: reasoningEndedAtByIdRef.current
      });
    };
    $[5] = arePreviewUrlsError;
    $[6] = arePreviewUrlsLoading;
    $[7] = csvFilenameByGcsUri;
    $[8] = currentChatId;
    $[9] = currentUserDetails;
    $[10] = failedPreviewGcsUris;
    $[11] = filenameByGcsUri;
    $[12] = isCsvFilenameIndexError;
    $[13] = isCsvFilenameIndexLoading;
    $[14] = isLoading;
    $[15] = justSentImagePreviewByGcsUri;
    $[16] = pageElements;
    $[17] = previewUrlByGcsUri;
    $[18] = refreshPreviewUrl;
    $[19] = signPreviewUrlWithoutCaching;
    $[20] = theme;
    $[21] = t4;
  } else {
    t4 = $[21];
  }
  return t4;
};
function _temp(image) {
  return image.gcsUri;
}
function _temp2(message) {
  return message.role === "user" ? extractAttachedImagesFromUserContent(message.content).map(_temp) : [];
}
function _temp3(markdown) {
  return extractMarkdownTables(markdown);
}
function _temp4(props) {
  return <Markdown variant="compact" extractTables={_temp3} {...props} />;
}